import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle2, XCircle, Loader2, Calendar, Clock, MapPin, ShieldCheck, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOAST_OPTIONS = {
  style: { background: "#F9F7F2", border: "1px solid #B1D8B7", color: "#1A1A1A" },
};

function formatDateLabel(dateStr) {
  if (!dateStr) return "(date pending)";
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
}

function formatTime12h(t) {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${mm} ${ampm}`;
}

export default function PublicAppointmentPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const fetchAppointment = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/appointment/${token}`);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Appointment not found or link expired");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAppointment();
  }, [fetchAppointment]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointment/${token}/respond`, { action: "confirm" });
      toast.success("Appointment confirmed. Thank you!");
      await fetchAppointment();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointment/${token}/respond`, {
        action: "decline",
        reason: declineReason.trim() || "",
      });
      toast.success("Response recorded. Our office will reach out soon.");
      setShowDeclineModal(false);
      await fetchAppointment();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="appointment-loading">
        <Loader2 className="w-8 h-8 animate-spin text-forest-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6" data-testid="appointment-error">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-stone-200 p-8 text-center shadow-sm">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-stone-900 mb-2">Link unavailable</h1>
          <p className="text-sm text-stone-600">{error}</p>
        </div>
      </div>
    );
  }

  const status = data?.confirmation_status || "waiting";
  const dateLabel = formatDateLabel(data?.scheduled_date);
  const timeLabel = data?.start_time && data?.end_time
    ? `${formatTime12h(data.start_time)} - ${formatTime12h(data.end_time)}`
    : data?.start_time
      ? formatTime12h(data.start_time)
      : "(time pending)";
  const services = data?.service_labels || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100" data-testid="public-appointment-page">
      <Toaster position="top-center" toastOptions={TOAST_OPTIONS} />

      {/* Header */}
      <div className="bg-forest-700 text-white py-6 px-5 shadow-sm">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-forest-100/80">Georgia Outdoor Worx</p>
            <h1 className="text-lg font-semibold mt-0.5">Appointment Confirmation</h1>
          </div>
          <ShieldCheck className="w-7 h-7 text-forest-200" />
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 py-6 space-y-5">
        {/* Greeting */}
        <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm" data-testid="appointment-card">
          <p className="text-sm text-stone-600 mb-1">Hello,</p>
          <h2 className="text-xl font-semibold text-stone-900" data-testid="appointment-client-name">
            {data?.client_name || "Customer"}
          </h2>
          <p className="text-sm text-stone-600 mt-3 leading-relaxed">
            We have a service scheduled for your property. Please confirm your availability for the
            date and time below.
          </p>
        </div>

        {/* Details */}
        <div className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm space-y-4" data-testid="appointment-details">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-forest-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">Date</p>
              <p className="text-base font-semibold text-stone-900" data-testid="appointment-date">{dateLabel}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-forest-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">Estimated Time</p>
              <p className="text-base font-semibold text-stone-900" data-testid="appointment-time">{timeLabel}</p>
            </div>
          </div>
          {services.length > 0 && (
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-forest-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">Service{services.length > 1 ? "s" : ""}</p>
                <ul className="text-base font-medium text-stone-900 space-y-0.5" data-testid="appointment-services">
                  {services.map((s, i) => (
                    <li key={i}>• {s}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Status / Actions */}
        {status === "waiting" && (
          <div className="space-y-3" data-testid="appointment-actions">
            <button
              onClick={handleConfirm}
              disabled={submitting}
              data-testid="appointment-confirm-btn"
              className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold py-4 rounded-xl shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
              Confirm Appointment
            </button>
            <button
              onClick={() => setShowDeclineModal(true)}
              disabled={submitting}
              data-testid="appointment-decline-btn"
              className="w-full bg-stone-100 hover:bg-stone-200 active:bg-stone-300 text-stone-700 font-semibold py-4 rounded-xl border border-stone-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-base"
            >
              <XCircle className="w-5 h-5" />
              Decline / Reschedule
            </button>
            <p className="text-xs text-stone-500 text-center mt-2 leading-relaxed">
              Confirming your appointment ensures our team is prepared. If you decline, our office
              will reach out shortly to find a better time.
            </p>
          </div>
        )}

        {status === "confirmed" && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center" data-testid="appointment-confirmed-state">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-green-900 mb-1">Appointment Confirmed</h3>
            <p className="text-sm text-green-800">
              Thank you! Our team will be ready on the scheduled date.
            </p>
          </div>
        )}

        {status === "declined" && (
          <div className="bg-stone-100 border border-stone-200 rounded-2xl p-6 text-center" data-testid="appointment-declined-state">
            <XCircle className="w-12 h-12 text-stone-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-stone-900 mb-1">Response Received</h3>
            <p className="text-sm text-stone-700">
              Our office will contact you shortly to find a better time.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-4 pb-6 space-y-2 text-xs text-stone-500">
          <div className="flex items-center justify-center gap-4">
            <a href="tel:4784182629" className="flex items-center gap-1 hover:text-forest-700">
              <Phone className="w-3.5 h-3.5" /> 478.418.2629
            </a>
            <a href="mailto:contactus@georgiaoutdoorworx.com" className="flex items-center gap-1 hover:text-forest-700">
              <Mail className="w-3.5 h-3.5" /> Email Us
            </a>
          </div>
          <p>Georgia Outdoor Worx LLC · Fully Licensed and Insured</p>
        </div>
      </div>

      {/* Decline Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="decline-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <XCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-stone-900">Decline this appointment?</h3>
                <p className="text-sm text-stone-600 mt-1 leading-relaxed">
                  Our office will reach out to reschedule. You can leave a quick note if you'd like
                  to suggest a better day or time.
                </p>
              </div>
            </div>
            <textarea
              data-testid="decline-reason-input"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="(Optional) Reason or preferred reschedule..."
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-forest-300 focus:border-forest-500"
            />
            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={() => { setShowDeclineModal(false); setDeclineReason(""); }}
                disabled={submitting}
                data-testid="decline-cancel-btn"
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDecline}
                disabled={submitting}
                data-testid="decline-confirm-btn"
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Yes, Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
